/**
 * useAddServiceEntry — enqueue-first, then best-effort POST.
 *
 * Durability: every entry is written to the offline queue (IndexedDB) BEFORE
 * the network call is attempted. If offline or if the network call fails for
 * connectivity/server reasons, the entry stays queued for later drain.
 *
 * Idempotency: a clientMutationId (UUID) is generated per entry and sent in
 * the POST body. The server dedups on this key scoped to authenticated
 * uid + vehicle, so a replay after a lost 200 response never creates a dupe.
 *
 * PII-safe logging (§00 SOC2): catch blocks log ONLY non-PII metadata
 * (clientMutationId, vehicleId, status, error code/status). Never log the
 * entry body or a raw ApiError (it carries message/details that can echo
 * field values such as performedBy, notes, vendor).
 *
 * source MUST be "manual" | "shop_invoice" — the server rejects "self_performed".
 *
 * Spec 011 (Slice 1) — before/after photos: `beforePhotoId`/`afterPhotoId`
 * are plain photo id strings (lamport's revised DTO — the server resolves
 * each id to its authoritative {photoId, sha256} and seals that; the client
 * never builds or sees a sha256). A caller with a still-uploading/
 * still-processing photo passes `pendingPhotoRefs` instead of an id for that
 * slot. When `pendingPhotoRefs` has any entry, `add()` skips its own
 * immediate online POST attempt entirely — enqueuing is durability-first,
 * and only useServiceQueue.ts's drain (which knows how to resolve pending
 * refs) is allowed to actually send this entry, to avoid a race between this
 * function's fast path and the drain's resolve-then-POST path.
 *
 * Spec 011 (Slice 2) — `laborHoursSource` is a SELF-ASSERTED provenance
 * label ("photo_estimated" | "edited"), computed entirely client-side from
 * the before/after photos' EXIF timestamps (see laborHoursSuggestion.ts) and
 * never re-derived by the server. Only sent when set to a non-default value
 * — omitting it (the "manual" case, or "no suggestion was ever offered")
 * defaults server-side to "manual". The server rejects
 * "photo_estimated"/"edited" without an accompanying `laborHours` — see
 * ServiceLog.tsx's onSubmit for how the two are kept in lockstep.
 *
 * 409 handling: the server rejects an un-committed/un-stripped photo id with
 * 409 + `error.code === "PHOTO_NOT_READY"` (also the backward-compat case of
 * a pre-cutover photo that can never become committed under the new strip
 * semantics). Keyed on the code, not bare HTTP 409, so this can't collide
 * with an unrelated 409 (e.g. an idempotency conflict). Both callers gate on
 * `status === 'committed'` before ever sending an id, so this should be
 * rare — but when it happens, surface it as a re-upload prompt (lamport),
 * never a dead/generic error: the fix is to remove the offending photo and
 * attach a fresh one, not to retry the same id.
 */

import { ApiError, apiFetch } from '@/lib/api';
import { auth } from '@/lib/firebase';
import { ServiceEntrySchema } from '@/lib/schemas';
import {
  enqueue,
  PHOTO_NOT_READY_CODE,
  PHOTO_NOT_READY_MESSAGE,
  remove,
  update,
  type PendingPhotoRef,
} from '@/lib/serviceQueue';
import { track } from '@/lib/telemetry';
import { useCallback, useState } from 'react';

/** Fields the caller supplies when adding a new service entry. */
export interface NewServiceEntry {
  summary: string;
  performedAt?: string;
  mileage?: number;
  systems?: string[];
  notes?: string;
  performedBy?: string;
  laborHours?: number;
  /** Spec 011 (Slice 2) — omit for "manual" (the server default). */
  laborHoursSource?: 'photo_estimated' | 'edited';
  source?: 'manual' | 'shop_invoice';
  vendor?: string;
  invoiceImageRef?: string;
  lineItems?: Array<{
    description: string;
    kind?: 'part' | 'labor' | 'fee' | 'tax' | 'shipping' | 'discount' | 'other';
    partNumber?: string;
    quantity?: number;
    amountMinor: number;
    currency?: string;
  }>;
  totalMinor?: number;
  currency?: string;
  supersedesEntryId?: string;
  /** Spec 011 — set only once the photo shows `status === 'committed'`. */
  beforePhotoId?: string;
  /** Spec 011 — set only once the photo shows `status === 'committed'`. */
  afterPhotoId?: string;
  /** Spec 011 — independent of `source`/`EntrySource`; default false. */
  selfPerformed?: boolean;
}

interface AddArgs {
  vehicleId: string;
  /** @deprecated — server stamps createdBy; passed for legacy callers but NOT sent in body. */
  createdBy?: string;
  entry: NewServiceEntry;
  /**
   * Spec 011 — before/after slots not yet resolved to a committed photo id.
   * Presence of either role defers the actual POST to useServiceQueue.ts's
   * drain (see file header) instead of add()'s own immediate fast path.
   */
  pendingPhotoRefs?: {
    before?: PendingPhotoRef;
    after?: PendingPhotoRef;
  };
}

export function useAddServiceEntry() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const add = useCallback(
    async ({ vehicleId, entry, pendingPhotoRefs }: AddArgs): Promise<string> => {
      setPending(true);
      setError(null);

      const clientMutationId = crypto.randomUUID();
      const uid = auth.currentUser?.uid ?? '';
      const queuedAt = new Date().toISOString();
      const hasPendingPhotoRefs = !!(pendingPhotoRefs?.before || pendingPhotoRefs?.after);

      // 1. Enqueue first — durability before network.
      await enqueue({
        clientMutationId,
        uid,
        vehicleId,
        entry,
        queuedAt,
        status: 'pending',
        ...(hasPendingPhotoRefs ? { pendingPhotoRefs } : {}),
      });

      const offlineQueued = !navigator.onLine;

      // Track the logging event — fire-and-forget; swallowed inside track().
      track('service_entry_logged', {
        had_photos: !!entry.invoiceImageRef || !!entry.beforePhotoId || !!entry.afterPhotoId,
        had_cost: typeof entry.totalMinor === 'number' || (entry.lineItems?.length ?? 0) > 0,
        offline_queued: offlineQueued,
      });

      // 2. Optimistic ID is returned immediately for the provisional UI.
      // If online AND every attached photo is already resolved, attempt the
      // POST directly and drain this item on success. A still-resolving
      // before/after photo (spec 011) skips this fast path entirely — the
      // useServiceQueue.ts drain owns resolving + sending in that case, so
      // this function never races it with a second, refless POST attempt.
      if (navigator.onLine && !hasPendingPhotoRefs) {
        try {
          await apiFetch(`/v1/vehicles/${vehicleId}/service`, {
            method: 'POST',
            body: {
              summary: entry.summary,
              clientMutationId,
              ...(entry.performedAt ? { performedAt: entry.performedAt } : {}),
              ...(typeof entry.mileage === 'number' ? { mileage: entry.mileage } : {}),
              ...(entry.systems && entry.systems.length > 0 ? { systems: entry.systems } : {}),
              ...(entry.notes ? { notes: entry.notes } : {}),
              ...(entry.performedBy ? { performedBy: entry.performedBy } : {}),
              ...(typeof entry.laborHours === 'number' ? { laborHours: entry.laborHours } : {}),
              ...(entry.laborHoursSource ? { laborHoursSource: entry.laborHoursSource } : {}),
              ...(entry.source ? { source: entry.source } : {}),
              ...(entry.vendor ? { vendor: entry.vendor } : {}),
              ...(entry.invoiceImageRef ? { invoiceImageRef: entry.invoiceImageRef } : {}),
              ...(entry.lineItems ? { lineItems: entry.lineItems } : {}),
              ...(typeof entry.totalMinor === 'number' ? { totalMinor: entry.totalMinor } : {}),
              ...(entry.currency ? { currency: entry.currency } : {}),
              ...(entry.supersedesEntryId ? { supersedesEntryId: entry.supersedesEntryId } : {}),
              ...(entry.beforePhotoId ? { beforePhotoId: entry.beforePhotoId } : {}),
              ...(entry.afterPhotoId ? { afterPhotoId: entry.afterPhotoId } : {}),
              ...(typeof entry.selfPerformed === 'boolean'
                ? { selfPerformed: entry.selfPerformed }
                : {}),
            },
            schema: ServiceEntrySchema,
          });
          // 2xx (200 or 201): server sealed it — remove from queue.
          await remove(clientMutationId);
        } catch (err) {
          // PII-safe log — never log entry body or raw ApiError.
          // ApiError.message / .details can echo submitted field values.
          const isApiError = err instanceof ApiError;
          const httpStatus = isApiError ? err.status : undefined;
          const code = isApiError ? err.code : undefined;

          if (isApiError && err.code === PHOTO_NOT_READY_CODE) {
            // Photo not yet committed/stripped (or a pre-cutover photo that
            // never will be) — a re-upload prompt, not a generic failure.
            // The form is still open here, so the user can literally use
            // the slot's Remove button and pick a new photo right away.
            await update(clientMutationId, { status: 'failed', failureReason: 'photo_not_ready' });
            console.warn('[serviceQueue] entry failed: photo not ready', {
              clientMutationId,
              vehicleId,
              status: 'failed',
              httpStatus,
              code,
            });
            const displayErr = new Error(PHOTO_NOT_READY_MESSAGE);
            setError(displayErr);
            setPending(false);
            throw displayErr;
          }

          if (isApiError && err.status >= 400 && err.status < 500 && err.status !== 401) {
            // 4xx (non-auth): validation error — mark failed, surface to caller.
            await update(clientMutationId, { status: 'failed' });
            console.warn('[serviceQueue] entry failed validation', {
              clientMutationId,
              vehicleId,
              status: 'failed',
              httpStatus,
              code,
            });
            const displayErr = new Error(isApiError ? err.message : 'Could not save entry');
            setError(displayErr);
            setPending(false);
            throw displayErr;
          }

          // Network / 5xx: leave queued (pending), drain will retry.
          // Do NOT throw — the entry is safely queued.
          console.warn('[serviceQueue] entry queued for retry', {
            clientMutationId,
            vehicleId,
            status: 'pending',
            httpStatus,
            code,
          });
        }
      }

      setPending(false);
      return clientMutationId;
    },
    [],
  );

  return { add, pending, error };
}
